import React, { useState } from 'react';

import AlertBanner from '~/components/core/AlertBanner';
import Heading from '~/components/core/TextComponents/Heading';
import Text from '~/components/core/TextComponents/Text';
import { PencilIcon } from '~/components/icons';
import InlineIconButton from '~/components/InlineIconButton';
import type { FundAccountEngineConfigInfo } from '~/server_shared/openapi/data-contracts';

import { TEXTS } from '../texts';

import { BEHAVIOR_LABELS, DEFAULT_BEHAVIOR, toDefaultBehavior } from './defaultBehavior';
import EditDefaultBehaviorDialog from './EditDefaultBehaviorDialog';

interface FundAccountDefaultBehaviorCardProps {
  organizationId: number;
  config: FundAccountEngineConfigInfo;
  onSaved: () => Promise<void> | void;
}

/** One read-only field of the summary. Kept as a local rather than a shared component: the two
 *  cells here are the only ones, and a definition-list helper would be surface with one caller. */
const SummaryField: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    {/* The key carries the weight, the value reads plain — it is the field name that has to be
        scannable when several of these sit side by side. */}
    <Text variant={Text.VARIANTS.XS} weight={Text.WEIGHTS.SEMI_BOLD} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
      {label}
    </Text>
    <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
      {value}
    </Text>
  </div>
);

const FundAccountDefaultBehaviorCard: React.FC<FundAccountDefaultBehaviorCardProps> = ({
  organizationId,
  config,
  onSaved,
}) => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const behavior = toDefaultBehavior(config.default_behavior);
  const isConfigured = behavior !== '';

  return (
    <div className="mb-20 rounded-md border border-solid border-slate-600 px-20 pb-20 pt-16">
      <div className="flex items-start justify-between gap-16">
        <div className="flex-1">
          <Heading variant={Heading.TYPES.H3} className="pb-4">
            {TEXTS.DEFAULT_BEHAVIOR_TITLE}
          </Heading>
          <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
            {TEXTS.DEFAULT_BEHAVIOR_SUBTITLE}
          </Text>

          {isConfigured ? (
            <div className="flex gap-32 pt-12">
              <SummaryField label={TEXTS.BEHAVIOR_SUMMARY_LABEL} value={BEHAVIOR_LABELS[behavior]} />
              {behavior === DEFAULT_BEHAVIOR.DefaultAccount ? (
                <SummaryField label={TEXTS.ACCOUNT_SUMMARY_LABEL} value={config.default_account_id ?? ''} />
              ) : null}
            </div>
          ) : (
            // An unset behavior is not a blank field but a blocked feature: it is what stops a
            // payment method selecting `source='rules'` at all, so the summary says so.
            <AlertBanner
              className="mt-12"
              alertType={AlertBanner.ALERT_TYPES.ERROR}
              title={TEXTS.ALERT_TITLE}
              note={TEXTS.NOT_CONFIGURED_WARNING}
              withIcon
            />
          )}

          {behavior === DEFAULT_BEHAVIOR.SendEmpty ? (
            // Shown on the summary and not only inside the dialog: this describes the standing
            // state of the live configuration, which an admin should meet on opening the tab
            // rather than discover on a failed approval.
            <AlertBanner
              className="mt-12"
              alertType={AlertBanner.ALERT_TYPES.WARNING}
              title={TEXTS.ALERT_TITLE}
              note={TEXTS.SEND_EMPTY_WARNING}
              withIcon
            />
          ) : null}
        </div>

        <InlineIconButton
          icon={PencilIcon}
          // `useIconButton` renders a real button carrying this label, rather than a bare clickable
          // icon — the affordance stays reachable by keyboard and addressable by name.
          useIconButton
          tooltipTitle={isConfigured ? TEXTS.EDIT_TOOLTIP : TEXTS.SET_UP_TOOLTIP}
          onClick={() => setIsEditOpen(true)}
        />
      </div>

      {isEditOpen ? (
        <EditDefaultBehaviorDialog
          organizationId={organizationId}
          config={config}
          onSaved={onSaved}
          onClose={() => setIsEditOpen(false)}
        />
      ) : null}
    </div>
  );
};

export default FundAccountDefaultBehaviorCard;
